import {Injectable} from '@angular/core';
import {MapLayersManager} from '../map-layers-manager';
import {RoutesService} from '../../../../data/routes/routes.service';
import { DataDrivenPropertyValueSpecification, LineLayerSpecification, LngLatBounds, LngLatLike } from 'maplibre-gl';
import {Subscription} from 'rxjs';
import {
  RouteConfiguration,
  RouteConfigurationWithSchema,
  RouteHierarchyItem,
  RouteHierarchyItemWithPath,
} from '../../../models/route';
import {MapFilters} from '../../../../configuration/map-filters';
import {RoadStatusCoverageLayerService} from './road-status-coverage-layer.service';
import {RoutesManagerService} from '../../../../data/routes/routes-manager.service';
import {MapControlService} from './map-control.service';
import {LineString} from 'geojson';
import {RouteSourceService} from './route-source.service';
import {ISettingKeyValuePair, SettingsService, StatusLayerType} from '../../../../configuration/settings.service';
import {MapStyles} from '../../../../configuration/map-styles';
import {RouteAssignmentManagerService} from '../../../../data/routes/route-assignment-manager.service';
import moment from 'moment';
import {RouteAssignment} from '../../../models/route-assignment';
import { ConfigurationService } from '../../../../configuration/configuration.service';
import { TrackStyles } from '../../../../configuration/model/TrackStyles';
import { LineStyle } from '../../../models/styling';

@Injectable({
  providedIn: 'root',
})
export class RoutesLayerService {
  static readonly LAYER_ID_ROUTES_PREFIX = 'routes-layer';

  // age in hours (how many hours ago) to consider route serviced
  routeStatusThreshold = 4;

  private mapLayersManager: MapLayersManager;
  private isEnabled: boolean;
  private isVisible = true;
  private routeConfigLayers: LineLayerSpecification[] = [];
  private routes: RouteHierarchyItemWithPath[];
  private routeIdFilter: number[];
  private layersInitialized = false;
  private routeConfigId: number;
  private routeConfigurations: RouteConfigurationWithSchema[];
  private settingsVisibility = false;
  private componentVisibility = false;
  private recentlyCompletedAssignmentKeys: string[] = [];
  private inProgressAssignmentKeys: string[] = [];
  private recentAssignments: RouteAssignment[] = [];
  private highlightedRoute: { configId: number; routeId: number };
  private contentVisibilityCutoff: Date = null;
  private trackStyles: TrackStyles;

  private readonly openSubscriptions = Array<Subscription>();

  static getColorExpression(
    routeConfigurationWSchema: RouteConfigurationWithSchema,
    globalColor: string,
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<string> {
    const configuration = routeConfigurationWSchema.configuration;
    if (routeConfigurationWSchema.schema.subRouteAttribute && configuration.subRouteItems && configuration.subRouteItems.length > 0) {
      // attribute based styling
      const expr: any[] = ['case'];
      configuration.subRouteItems.forEach(subRouteItem => {
        expr.push(['==', ['get', 'subrouteid'], subRouteItem.subRouteId]);
        expr.push(subRouteItem.style.color);
      });
      expr.push(globalColor);
      return expr as DataDrivenPropertyValueSpecification<string>;
    } else {
      const routeColorOverrides = routes.filter(route => route.configId === routeConfigurationWSchema.id && !!route.styleOverride?.color);
      if (routeColorOverrides.length === 0) {
        // single styling
        return globalColor;
      }
      // individual styling
      const colorExpression: any[] = ['case'];
      routeColorOverrides.forEach(routeColorOverride => {
        colorExpression.push(['==', ['get', 'routeid'], routeColorOverride.routeId]);
        colorExpression.push(routeColorOverride.styleOverride.color);
      });
      colorExpression.push(globalColor);
      return colorExpression as DataDrivenPropertyValueSpecification<string>;
    }
  }

  static getWidthExpression(
    routeConfigurationWSchema: RouteConfigurationWithSchema,
    globalWidth: number,
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<number> {
    const configuration = routeConfigurationWSchema.configuration;
    if (routeConfigurationWSchema.schema.subRouteAttribute && configuration.subRouteItems && configuration.subRouteItems.length > 0) {
      // attribute based styling
      const expr: any[] = ['case'];
      configuration.subRouteItems.forEach(subRouteItem => {
        expr.push(['==', ['get', 'subrouteid'], subRouteItem.subRouteId]);
        expr.push(subRouteItem.style.width);
      });
      expr.push(globalWidth);
      return expr as DataDrivenPropertyValueSpecification<number>;
    } else {
      const widthOverrides = routes.filter(route => route.configId === routeConfigurationWSchema.id && !!route.styleOverride?.width);
      if (widthOverrides.length === 0) {
        // single styling
        return globalWidth;
      }
      // individual styling
      const expr: any[] = ['case'];
      widthOverrides.forEach(widthOverride => {
        expr.push(['==', ['get', 'routeid'], widthOverride.routeId]);
        expr.push(widthOverride.styleOverride.width);
      });
      expr.push(globalWidth);
      return expr as DataDrivenPropertyValueSpecification<number>;
    }
  }

  static getOpacityExpression(
    routeConfigurationWSchema: RouteConfigurationWithSchema,
    globalOpacity: number,
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<number> {
    const configuration = routeConfigurationWSchema.configuration;
    if (routeConfigurationWSchema.schema.subRouteAttribute && configuration.subRouteItems && configuration.subRouteItems.length > 0) {
      // attribute based styling
      const expr: any[] = ['case'];
      configuration.subRouteItems.forEach(subRouteItem => {
        expr.push(['==', ['get', 'subrouteid'], subRouteItem.subRouteId]);
        expr.push(subRouteItem.style.opacity);
      });
      expr.push(globalOpacity);
      return expr as DataDrivenPropertyValueSpecification<number>;
    } else {
      const opacityOverrides = routes.filter(route => route.configId === routeConfigurationWSchema.id && !!route.styleOverride?.opacity);
      if (opacityOverrides.length === 0) {
        // single styling
        return globalOpacity;
      }
      // individual styling
      const expr: any[] = ['case'];
      opacityOverrides.forEach(opacityOverride => {
        expr.push(['==', ['get', 'routeid'], opacityOverride.routeId]);
        expr.push(opacityOverride.styleOverride.opacity);
      });
      expr.push(globalOpacity);
      return expr as DataDrivenPropertyValueSpecification<number>;
    }
  }

  static getLineTypeExpression(
    routeConfigurationWSchema: RouteConfigurationWithSchema,
    globalLineType: number[],
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<number[]> {
    const fallbackDashArray = [10];
    const configuration = routeConfigurationWSchema.configuration;
    if (routeConfigurationWSchema.schema.subRouteAttribute && configuration.subRouteItems && configuration.subRouteItems.length > 0) {
      // attribute based styling
      const expr: any[] = ['case'];
      configuration.subRouteItems.forEach(subRouteItem => {
        expr.push(['==', ['get', 'subrouteid'], subRouteItem.subRouteId]);
        expr.push(['literal', subRouteItem.style.lineType ?? fallbackDashArray]);
      });
      expr.push(['literal', globalLineType ?? fallbackDashArray]);
      return expr as DataDrivenPropertyValueSpecification<number[]>;
    } else {
      const lineTypeOverrides = routes.filter(route => route.configId === routeConfigurationWSchema.id && !!route.styleOverride?.lineType);
      if (lineTypeOverrides.length === 0) {
        // single styling
        return ['literal', globalLineType ?? fallbackDashArray];
      }
      // individual styling
      const expr: any[] = ['case'];
      lineTypeOverrides.forEach(lineTypeOverride => {
        expr.push(['==', ['get', 'routeid'], lineTypeOverride.routeId]);
        expr.push(['literal', lineTypeOverride.styleOverride.lineType ?? fallbackDashArray]);
      });
      expr.push(['literal', globalLineType ?? fallbackDashArray]);
      return expr as DataDrivenPropertyValueSpecification<number[]>;
    }
  }

  constructor(
    private routesService: RoutesService,
    private routesManager: RoutesManagerService,
    private routeSourceService: RouteSourceService,
    private mapControlService: MapControlService,
    private settingsService: SettingsService,
    private routeAssignmentManager: RouteAssignmentManagerService,
    private configurationService: ConfigurationService
  ) {}

  init(mapLayersManager: MapLayersManager, isEnabled: boolean) {
    if (!!this.mapLayersManager) {
      throw Error('The map layers manager has already been set.');
    }
    this.isEnabled = isEnabled;
    this.mapLayersManager = mapLayersManager;
    this.trackStyles = this.configurationService.trackStyles;
    this.routeConfigurations = isEnabled ? this.routesManager.getRouteConfigurations() : [];
    this.routes = isEnabled ? this.routesManager.getRouteHierarchyLeaves() : null;
    this.reload();
    if (this.isEnabled) {
      this.connectToManager();
    }
  }

  release() {
    if (!this.mapLayersManager) {
      throw Error('The map has not been set!');
    }

    for (const subscription of this.openSubscriptions) {
      subscription.unsubscribe();
    }
    this.openSubscriptions.length = 0;
    this.mapLayersManager = null;
    this.layersInitialized = false;
    this.routeConfigLayers = [];
    this.routeConfigId = undefined;
    this.routeIdFilter = undefined;
    this.routes = undefined;
    this.routeConfigurations = undefined;
  }

  // on init or on map style change
  reload() {
    if (this.isEnabled) {
      this.loadLayers();
    }
  }

  getRoutes() {
    if (this.layersInitialized) {
      return this.routes;
    } else {
      return null;
    }
  }

  getRouteConfigurationLayers() {
    if (this.layersInitialized) {
      return this.routeConfigLayers;
    } else {
      return null;
    }
  }

  highlightRoute(configId: number, routeId: number) {
    this.highlightedRoute = configId && routeId ? { configId, routeId } : null;
    if (this.layersInitialized) {
      this.updateStyle();
    }
  }

  private loadLayers() {
    if (!!this.routeConfigurations) {
      this.routeConfigLayers = [];
      if (this.routes && this.routes.length > 0) {
        // initialize layers
        const routeFilter = this.routesManager.getRouteFilterSnapshot();
        this.routeConfigId = routeFilter.configId;
        this.routeIdFilter = routeFilter.routeIds;

        this.isVisible = this.routesManager.getVisibilitySnapshot();
        const isVisible = this.isVisible ? 'visible' : 'none';
        this.getRouteConfigLayers().forEach(layer => {
          layer.layout.visibility = isVisible;
          this.routeConfigLayers.push(layer);
          this.mapLayersManager.addLayer(layer);
          this.mapLayersManager.moveLayer(layer.id, `${RoadStatusCoverageLayerService.LAYER_ID_PREFIX}-5`);
        });

        this.layersInitialized = true;
      }
    }
  }

  private handleLayerVisibilityChange() {
    const makeVisible = this.componentVisibility || this.settingsVisibility;
    if (this.layersInitialized) {
      this.routeConfigLayers.forEach(lineLayer => {
        this.mapLayersManager.setLayerVisibility(lineLayer.id, makeVisible);
      });
    }
  }

  private handleFilterChange() {
    // get routeId from layer ID
    if (this.layersInitialized) {
      this.routeConfigLayers.forEach(lineLayer => {
        const splitLayerId = lineLayer.id.split('_');
        this.mapLayersManager.setFilter(
          lineLayer.id,
          MapFilters.getRouteConfigFilter(+splitLayerId[1], this.routeConfigId, this.routeIdFilter)
        );
      });

      // zoom to geometries
      const routeGeometries = this.routeSourceService.getFeatureCollection();
      if (!!this.routeConfigId && !!routeGeometries) {
        const coordinates = routeGeometries.features
          .filter(feature => this.routeConfigId === feature.properties['configid'])
          .filter(feature => this.routeIdFilter.indexOf(feature.properties['routeid']) !== -1)
          .map(feature => {
            const lineString = feature.geometry as LineString;
            return lineString.coordinates;
          })
          .reduce((accumulator, value) => accumulator.concat(value), []); // flatten array

        if (coordinates.length > 0) {
          const bounds = new LngLatBounds([coordinates[0][0], coordinates[0][1]], [coordinates[0][0], coordinates[0][1]]);

          for (const coord of coordinates) {
            bounds.extend(coord as LngLatLike);
          }
          this.mapControlService.fitMapToBounds(bounds);
        }
      }
    }
  }

  private handleModeChanged() {
    if (this.layersInitialized) {
      this.updateStyle();
    }
  }

  private getRouteConfigLayers(): LineLayerSpecification[] {
    return this.routeConfigurations.map(routeConfiguration => {
      let globalRouteStyle = routeConfiguration?.schema?.style;
      if (!globalRouteStyle) {
        console.warn('Missing schema style. This should not happen!');
        globalRouteStyle = new LineStyle('#FF0000', 4, 1.0);
      }
      const routes = this.routes?.filter(route => route.configId === routeConfiguration.id);
      return {
        id: `${RoutesLayerService.LAYER_ID_ROUTES_PREFIX}_${routeConfiguration.id}`,
        type: 'line',
        source: RouteSourceService.ROUTES_SOURCE_ID,
        filter: MapFilters.getRouteConfigFilter(routeConfiguration.id, this.routeConfigId, this.routeIdFilter),
        layout: {
          'line-join': 'miter',
          'line-cap': 'butt',
          'line-miter-limit': 2,
        },
        paint: {
          'line-color': this.getColorStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.color, routes),
          'line-width': this.getWidthStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.width, routes),
          'line-opacity': this.getOpacityStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.opacity, routes),
          'line-dasharray': this.getLineStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.lineType, routes),
        },
      } as LineLayerSpecification;
    });
  }

  private updateStyle() {
    this.routeConfigLayers.forEach(routeConfigurationLayer => {
      const splitLayerId = routeConfigurationLayer.id.split('_');
      let globalRouteStyle = this.routeConfigurations.find(config => config.id === +splitLayerId[1])?.schema?.style;
      if (!globalRouteStyle) {
        console.warn('Missing schema style. This should not happen!');
        globalRouteStyle = new LineStyle('#FF0000', 4, 1.0);
      }
      const routeConfiguration = this.routeConfigurations.find(r => r.id === +splitLayerId[1]);
      const routes = this.routes?.filter(route => route.configId === +splitLayerId[1]);
      this.mapLayersManager.setLayerPaintProperty(
        routeConfigurationLayer.id,
        'line-color',
        this.getColorStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.color, routes)
      );
      this.mapLayersManager.setLayerPaintProperty(
        routeConfigurationLayer.id,
        'line-width',
        this.getWidthStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.width, routes)
      );
      this.mapLayersManager.setLayerPaintProperty(
        routeConfigurationLayer.id,
        'line-opacity',
        this.getOpacityStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.opacity, routes)
      );
      this.mapLayersManager.setLayerPaintProperty(
        routeConfigurationLayer.id,
        'line-dasharray',
        this.getLineStylePerRouteConfiguration(routeConfiguration, globalRouteStyle.lineType, routes)
      );
    });
  }

  private filterRecentAssignments() {
    this.recentlyCompletedAssignmentKeys = this.recentAssignments
      .filter(assignment => {
        return (
          !this.contentVisibilityCutoff ||
          (!!assignment.completed && moment(assignment.completed).isAfter(moment(this.contentVisibilityCutoff)))
        );
      })
      .filter(assignment => {
        return (
          !!assignment.completed &&
          !assignment.cancelled &&
          moment(assignment.completed).isAfter(moment().subtract(this.routeStatusThreshold, 'hour'))
        );
      })
      .map(assignment => `${assignment.configId}_${assignment.routeId}`);
    this.inProgressAssignmentKeys = this.recentAssignments
      .filter(assignment => !assignment.completed && !!assignment.onAssignmentFrom)
      .map(assignment => `${assignment.configId}_${assignment.routeId}`);
  }

  connectToManager() {
    const routesFilterSubscription = this.routesManager.routeFilter$.subscribe(filterUpdate => {
      this.routeConfigId = filterUpdate.configId;
      this.routeIdFilter = filterUpdate.routeIds;
      this.handleFilterChange();
    });
    this.openSubscriptions.push(routesFilterSubscription);

    const routesVisibilitySubscription = this.routesManager.basedOnRouteVisibility$.subscribe(visible => {
      const componentVisibilityChanged = this.componentVisibility !== visible;
      this.componentVisibility = visible;
      this.handleLayerVisibilityChange();
      if (componentVisibilityChanged) {
        this.handleModeChanged();
      }
    });
    this.openSubscriptions.push(routesVisibilitySubscription);

    const trackStylesSubscription = this.configurationService.userTrackStyles$.subscribe(styles => {
      this.trackStyles = styles;
      this.updateStyle();
    });
    this.openSubscriptions.push(trackStylesSubscription);

    const settingsChangedSubscription = this.settingsService.settingsChangedObservable.subscribe((newSettings: ISettingKeyValuePair) => {
      if (newSettings.key === SettingsService.TRACKS_LAYER_TYPE_KEY) {
        this.settingsVisibility = newSettings.value === StatusLayerType.ROUTE_STATUS;
        this.handleLayerVisibilityChange();
      }
      if (newSettings.key === SettingsService.ROUTE_STATUS_THRESHOLD) {
        this.routeStatusThreshold = +newSettings.value;
        if (this.layersInitialized) {
          this.filterRecentAssignments();
          this.updateStyle();
        }
      }
    });
    this.openSubscriptions.push(settingsChangedSubscription);

    const assignmentsSubscription = this.routeAssignmentManager.recentRouteAssignments$.subscribe(assignmentsUpdate => {
      this.recentAssignments = assignmentsUpdate.state;
      if (this.layersInitialized) {
        this.filterRecentAssignments();
        this.updateStyle();
      }
    });
    this.openSubscriptions.push(assignmentsSubscription);

    this.openSubscriptions.push(
      this.configurationService.sharedConfigurationModel.subscribe(model => {
        if (model) {
          this.contentVisibilityCutoff = model.contentVisibilityCutoff;
          this.filterRecentAssignments();
          this.mapLayersManager.waitUntilStyleLoaded(() => this.updateStyle());
        }
      })
    );
  }

  private getColorStylePerRouteConfiguration(
    routeConfiguration: RouteConfigurationWithSchema,
    globalColor: string,
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<string> {
    if (!routes || routes.length === 0) {
      // fallback
      console.warn('Routes not initialized!');
      return this.trackStyles.routeStatusNotServiced.color;
    }
    if (this.componentVisibility) {
      return RoutesLayerService.getColorExpression(routeConfiguration, globalColor, routes);
    } else {
      // status styling
      const inProgressRouteIds = this.inProgressAssignmentKeys
        .filter(key => key.startsWith(`${routeConfiguration.id}_`))
        .map(key => +key.substring(key.indexOf('_') + 1));
      const recentlyCompletedRouteIds = this.recentlyCompletedAssignmentKeys
        .filter(key => key.startsWith(`${routeConfiguration.id}_`))
        .map(key => +key.substring(key.indexOf('_') + 1));
      if (
        inProgressRouteIds.length === 0 &&
        recentlyCompletedRouteIds.length === 0 &&
        (!this.highlightedRoute || this.highlightedRoute?.configId !== routeConfiguration.id)
      ) {
        return this.trackStyles.routeStatusNotServiced.color;
      } else {
        const colorExpression: any[] = ['case'];
        if (!!this.highlightedRoute && this.highlightedRoute.configId === routeConfiguration.id) {
          colorExpression.push(['==', ['get', 'routeid'], this.highlightedRoute.routeId]);
          colorExpression.push(MapStyles.PAUSED_COLOR);
        }
        if (inProgressRouteIds.length > 0) {
          colorExpression.push(['in', ['get', 'routeid'], ['literal', inProgressRouteIds]]);
          colorExpression.push(this.trackStyles.routeStatusInProgress.color);
        }
        if (recentlyCompletedRouteIds.length > 0) {
          colorExpression.push(['in', ['get', 'routeid'], ['literal', recentlyCompletedRouteIds]]);
          colorExpression.push(this.trackStyles.routeStatusServiced.color);
        }
        colorExpression.push(this.trackStyles.routeStatusNotServiced.color);
        return colorExpression as DataDrivenPropertyValueSpecification<string>;
      }
    }
  }

  private getWidthExprPerRouteConfiguration(
    routeConfiguration: RouteConfigurationWithSchema,
    globalWidth: number,
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<number> {
    if (!routes || routes.length === 0) {
      // fallback
      console.warn('Routes not initialized!');
      return MapStyles.ROUTE_STATUS_WIDTH;
    }
    if (this.componentVisibility) {
      return RoutesLayerService.getWidthExpression(routeConfiguration, globalWidth, routes);
    } else {
      // status styling
      return MapStyles.ROUTE_STATUS_WIDTH;
    }
  }

  private getWidthStylePerRouteConfiguration(
    routeConfiguration: RouteConfigurationWithSchema,
    globalWidth: number,
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<number> {
    const expr = this.getWidthExprPerRouteConfiguration(routeConfiguration, globalWidth, routes);
    return [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      10,
      ['*', expr, ['^', 2, -1]],
      24,
      ['*', expr, ['^', 2, 8]],
    ] as DataDrivenPropertyValueSpecification<number>;
  }

  private getOpacityStylePerRouteConfiguration(
    routeConfiguration: RouteConfigurationWithSchema,
    globalOpacity: number,
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<number> {
    if (!routes || routes.length === 0) {
      // fallback
      console.warn('Routes not initialized!');
      return MapStyles.ROUTE_STATUS_OPACITY;
    }
    if (this.componentVisibility) {
      return RoutesLayerService.getOpacityExpression(routeConfiguration, globalOpacity, routes);
    } else {
      // status styling
      return MapStyles.ROUTE_STATUS_OPACITY;
    }
  }

  private getLineStylePerRouteConfiguration(
    routeConfiguration: RouteConfigurationWithSchema,
    globalLineType: number[],
    routes: RouteHierarchyItem[]
  ): DataDrivenPropertyValueSpecification<number[]> {
    const fallbackDashArray = [1, 0];
    if (!routes || routes.length === 0) {
      // fallback
      console.warn('Routes not initialized!');
      return ['literal', fallbackDashArray];
    }
    if (this.componentVisibility) {
      return RoutesLayerService.getLineTypeExpression(routeConfiguration, globalLineType, routes);
    } else {
      // status styling
      const inProgressRouteIds = this.inProgressAssignmentKeys
        .filter(key => key.startsWith(`${routeConfiguration.id}_`))
        .map(key => +key.substring(key.indexOf('_') + 1));
      const recentlyCompletedRouteIds = this.recentlyCompletedAssignmentKeys
        .filter(key => key.startsWith(`${routeConfiguration.id}_`))
        .map(key => +key.substring(key.indexOf('_') + 1));
      const expr: any[] = ['case'];
      // solid line for highlighted
      expr.push(['==', ['get', 'routeid'], this.highlightedRoute?.routeId ?? -1]);
      expr.push(['literal', fallbackDashArray]);
      // in progress
      if (inProgressRouteIds.length > 0) {
        expr.push(['in', ['get', 'routeid'], ['literal', inProgressRouteIds]]);
        expr.push(['literal', this.trackStyles.routeStatusInProgress.dasharray ?? fallbackDashArray]);
      }
      // serviced
      if (recentlyCompletedRouteIds.length > 0) {
        expr.push(['in', ['get', 'routeid'], ['literal', recentlyCompletedRouteIds]]);
        expr.push(['literal', this.trackStyles.routeStatusServiced.dasharray ?? fallbackDashArray]);
      }
      // not serviced
      expr.push(['literal', this.trackStyles.routeStatusNotServiced.dasharray ?? fallbackDashArray]);
      return expr as DataDrivenPropertyValueSpecification<number[]>;
    }
  }
}
